import { useEffect, useState } from 'react'
import { Bell, Check } from 'lucide-react'
import { Button } from '@/components'
import { cn } from '@/lib/utils'
import {
  LONG_RUN_AFTER_MS,
  longRunNoticeStore,
  requestNotifications,
  useLongRunNotice,
  type NoticePress,
} from '../longRunNotice'

const SPENT_LABEL: Record<NoticePress, string> = { notify: 'Notify me', dismiss: 'Don’t ask again' }

const NOTIFICATIONS_ON =
  "Notifications on - I'll let you know when this finishes. You can switch strategies or even close this tab, it keeps running."

interface LongRunNoticeProps {
  /** One notice per run: the tool message id. */
  noticeId: string
  /** When the run started; the ten seconds count from here, so a reload mid-run still counts. */
  startedAt?: string
  running: boolean
}

/**
 * The foot of a running card once its job has taken ten seconds: says so, and offers to
 * notify through the browser so the trader can leave (D-p3-696).
 */
export default function LongRunNotice({ noticeId, startedAt, running }: LongRunNoticeProps) {
  const notice = useLongRunNotice()
  const past10s = usePast(startedAt, LONG_RUN_AFTER_MS)

  if (!running) return null
  if (!past10s && !notice.enabled) return null

  const spent = notice.pressed[noticeId]
  const canAsk = notice.available && !notice.enabled && !notice.dismissedAsk && !spent

  return (
    <div className="mt-1.5 border-t border-border pt-2">
      {notice.enabled ? (
        <p className="flex items-start gap-1.5 text-caption text-muted-foreground">
          <Check size={12} strokeWidth={1.75} className="mt-px shrink-0" />
          <span>{NOTIFICATIONS_ON}</span>
        </p>
      ) : (
        <p className="text-caption text-muted-foreground">This one is taking longer than usual.</p>
      )}
      {spent && (
        <span className={cn('block text-caption text-muted-foreground', notice.enabled && 'pl-4.5')}>
          {/* Answered: a muted line on desktop, a spent chip below 2xl so the seat is not left bare. */}
          <span className="max-2xl:hidden">- {SPENT_LABEL[spent]}</span>
          <span className="hidden max-2xl:inline-flex items-center h-7 px-2.5 mt-2 rounded-md border border-border bg-muted/50 text-label text-muted-foreground align-middle">
            {SPENT_LABEL[spent]}
          </span>
        </span>
      )}
      {canAsk && (
        <div className="mt-2 flex items-center">
          <Button
            variant="outline"
            size="sm"
            className="h-7 shrink-0 self-start text-caption sm:self-auto motion-safe:active:scale-[0.97] motion-safe:transition-transform motion-safe:duration-100 max-sm:min-h-11"
            onClick={() => {
              longRunNoticeStore.press(noticeId, 'notify')
              void requestNotifications()
            }}
          >
            <Bell size={14} strokeWidth={1.75} />
            <span>Notify me</span>
          </Button>
          <button
            type="button"
            onClick={() => longRunNoticeStore.dismissAsk(noticeId)}
            className="ml-2 text-caption text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
          >
            {"Don't ask again"}
          </button>
        </div>
      )}
    </div>
  )
}

/** True once `ms` have passed since `from` (or since mount, without a start time). */
function usePast(from: string | undefined, ms: number) {
  const [start] = useState(() => {
    const parsed = from ? Date.parse(from) : NaN
    return Number.isNaN(parsed) ? Date.now() : parsed
  })
  const [past, setPast] = useState(() => Date.now() - start >= ms)

  useEffect(() => {
    if (past) return
    const id = window.setTimeout(() => setPast(true), Math.max(0, start + ms - Date.now()))
    return () => window.clearTimeout(id)
  }, [past, start, ms])

  return past
}
